import { Box, Paper, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import type { StrategicInsightsData } from '../../../services/tenderAIService';

interface AdvancedInsightsProps {
  data: StrategicInsightsData | null;
}

const AdvancedInsights = ({ data }: AdvancedInsightsProps) => {
  const theme = useTheme();

  if (!data?.aggregations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="text.secondary">No advanced insights data available.</Typography>
      </Box>
    );
  }

  // 1. Tender Type Data
  const tenderTypeData = data.aggregations.tenderType
    ? Object.entries(data.aggregations.tenderType)
        .map(([type, stats]) => ({
          type,
          winRate: stats.winRate,
          volume: stats.bidVolume,
        }))
        .filter((d) => d.volume > 0)
        .sort((a, b) => b.winRate - a.winRate)
    : [];

  // 2. Category Data
  const categoryData = data.aggregations.category
    ? Object.entries(data.aggregations.category)
        .map(([category, stats]) => ({
          category,
          winRate: stats.winRate,
          volume: stats.bidVolume,
        }))
        .filter((d) => d.volume > 0)
        .sort((a, b) => b.winRate - a.winRate)
    : [];

  // 3. Keyword Data (Stacked Bar Chart)
  const keywordData = data.aggregations.keyword
    ? Object.entries(data.aggregations.keyword)
        .filter(
          ([_, stats]: [
            string,
            { bidVolume: number; wins: number; active: number; other: number; losses: number },
          ]) => stats.bidVolume > 0
        )
        .sort(
          (a: [string, { bidVolume: number }], b: [string, { bidVolume: number }]) =>
            b[1].bidVolume - a[1].bidVolume
        ) // Sort by highest volume first
        .slice(0, 15) // Limit to top 15 keywords to prevent overcrowding
    : [];

  const keywordLabels = keywordData.map(([keyword]) => keyword);
  const keywordWins = keywordData.map(
    ([_, stats]: [
      string,
      { bidVolume: number; wins: number; active: number; other: number; losses: number },
    ]) => stats.wins
  );
  const keywordActive = keywordData.map(
    ([_, stats]: [
      string,
      { bidVolume: number; wins: number; active: number; other: number; losses: number },
    ]) => stats.active
  );
  const keywordOther = keywordData.map(
    ([_, stats]: [
      string,
      { bidVolume: number; wins: number; active: number; other: number; losses: number },
    ]) => stats.other
  );
  const keywordLosses = keywordData.map(
    ([_, stats]: [
      string,
      { bidVolume: number; wins: number; active: number; other: number; losses: number },
    ]) => stats.losses
  );

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 4 }}>
      {/* Tender Types Chart */}
      <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
        <Paper variant="outlined" sx={{ p: 2, height: 400 }}>
          <Typography variant="subtitle1" align="center" gutterBottom>
            Win Rate by Tender Type
          </Typography>
          {tenderTypeData.length > 0 ? (
            <BarChart
              dataset={tenderTypeData}
              xAxis={[
                {
                  scaleType: 'band',
                  dataKey: 'type',
                  label: 'Tender Type',
                  height: 120,
                  tickLabelStyle: {
                    angle: 45,
                    fontSize: 11,
                  },
                  tickPlacement: 'middle',
                  tickLabelPlacement: 'tick',
                },
              ]}
              yAxis={[{ label: 'Win Rate (%)' }]}
              series={[
                {
                  id: 'tenderType',
                  dataKey: 'winRate',
                  label: 'Win Rate (%)',
                  valueFormatter: (value) => `${value?.toFixed(1) || 0}%`,
                  barLabel: 'value',
                },
              ]}
              margin={{ left: 10, right: 20, top: 20, bottom: 10 }}
              sx={{
                '& .MuiBarLabel-series-tenderType': {
                  fill: theme.palette.primary.contrastText,
                  fontWeight: 500,
                },
              }}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="text.secondary">No tender type data</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Categories Chart */}
      <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
        <Paper variant="outlined" sx={{ p: 2, height: 400 }}>
          <Typography variant="subtitle1" align="center" gutterBottom>
            Win Rate by Category
          </Typography>
          {categoryData.length > 0 ? (
            <BarChart
              dataset={categoryData}
              xAxis={[
                {
                  scaleType: 'band',
                  dataKey: 'category',
                  label: 'Category',
                  height: 120,
                  tickLabelStyle: {
                    angle: 45,
                    fontSize: 11,
                  },
                  tickPlacement: 'middle',
                  tickLabelPlacement: 'tick',
                },
              ]}
              yAxis={[{ label: 'Win Rate (%)' }]}
              series={[
                {
                  id: 'category',
                  dataKey: 'winRate',
                  label: 'Win Rate (%)',
                  valueFormatter: (value) => `${value?.toFixed(1) || 0}%`,
                  color: '#2e7d32', // Distinct color
                  barLabel: 'value',
                },
              ]}
              margin={{ left: 10, right: 20, top: 20, bottom: 10 }}
              sx={{
                '& .MuiBarLabel-series-category': { fill: '#fff', fontWeight: 500 },
              }}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="text.secondary">No category data</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Keywords Stacked Bar */}
      <Box sx={{ gridColumn: 'span 12' }}>
        <Paper variant="outlined" sx={{ p: 2, height: 450 }}>
          <Typography variant="subtitle1" align="center" gutterBottom>
            Top Keywords Performance
          </Typography>
          {keywordLabels.length > 0 ? (
            <BarChart
              xAxis={[
                {
                  scaleType: 'band',
                  data: keywordLabels,
                  label: 'Keywords',
                  height: 120,
                  tickLabelStyle: {
                    angle: 45,
                    fontSize: 11,
                  },
                  tickPlacement: 'middle',
                  tickLabelPlacement: 'tick',
                },
              ]}
              yAxis={[{ label: 'Number of Tenders' }]}
              series={[
                {
                  id: 'wins',
                  data: keywordWins,
                  stack: 'volume',
                  label: 'Wins',
                  color: theme.palette.success.main,
                  barLabel: 'value',
                },
                {
                  id: 'active',
                  data: keywordActive,
                  stack: 'volume',
                  label: 'Active',
                  color: theme.palette.info.main,
                  barLabel: 'value',
                },
                {
                  id: 'other',
                  data: keywordOther,
                  stack: 'volume',
                  label: 'Other',
                  color: theme.palette.warning.main,
                  barLabel: 'value',
                },
                {
                  id: 'losses',
                  data: keywordLosses,
                  stack: 'volume',
                  label: 'Losses',
                  color: theme.palette.error.main,
                  barLabel: 'value',
                },
              ]}
              margin={{ left: 10, right: 20, top: 20, bottom: 10 }}
              sx={{
                '& .MuiBarLabel-series-wins': {
                  fill: theme.palette.success.contrastText,
                  fontWeight: 500,
                },
                '& .MuiBarLabel-series-active': {
                  fill: theme.palette.info.contrastText,
                  fontWeight: 500,
                },
                '& .MuiBarLabel-series-other': {
                  fill: theme.palette.warning.contrastText,
                  fontWeight: 500,
                },
                '& .MuiBarLabel-series-losses': {
                  fill: theme.palette.error.contrastText,
                  fontWeight: 500,
                },
              }}
              slotProps={{
                legend: {
                  position: { vertical: 'top', horizontal: 'center' },
                },
              }}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="text.secondary">No keyword data</Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AdvancedInsights;
